import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { Upload, FileText, Shield, ArrowLeft, ChevronDown } from 'lucide-react';
import domainConfig from './config/domainConfig';
import { generateStandardizedPDF, REPORT_SECTIONS } from './utils/pdfGenerator';
import ReportHeader from './components/ReportHeader';

const DefensiveVulnerabilityReportGenerator = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== 'text/csv' && !selectedFile.name.toLowerCase().endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleGenerateReport = async () => {
    if (!file) {
      setError('Please select a CSV file');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('csvFile', file);

      const response = await fetch(`${domainConfig.apiUrl}/api/reports/defensivevulnerability`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate report');
      }

      const data = await response.json();
      setReportData(data.report);
    } catch (error) {
      console.error('Error generating report:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const saveReportAsPDF = async () => {
    await generateStandardizedPDF(
      REPORT_SECTIONS.DEFENSIVE_VULNERABILITY,
      'defensive-vulnerability-report.pdf',
      setError
    );
  };

  const calculatePercentages = (counts, total) => {
    return Object.entries(counts).map(([key, value]) => ({
      category: key,
      count: value,
      percentage: total > 0 ? ((value / total) * 100).toFixed(1) : 0
    })).sort((a, b) => b.count - a.count);
  };

  const calculateRoutePercentages = (routeData, total) => {
    return Object.entries(routeData).map(([routeCode, data]) => {
      const avgDepth = data.depths.length > 0 ? (data.totalDepth / data.depths.length).toFixed(1) : 'unknown';
      const defenders = data.defenders.length > 0 ? [...new Set(data.defenders)].join(', ') : 'None listed';
      return {
        category: `${routeCode} (avg ${avgDepth} yards)`,
        count: data.count,
        percentage: total > 0 ? ((data.count / total) * 100).toFixed(1) : 0,
        defenders: defenders
      };
    }).sort((a, b) => b.count - a.count);
  };

  const getExplanation = (title) => {
    const explanations = {
      "Offensive Personnel": "Shows which offensive personnel groupings (11 = 1 RB, 1 TE, 3 WR) are most successful for explosive plays. Helps identify which packages the defense struggles against.",
      "Formation": "Analyzes which offensive formations (3x1, 2x2, etc.) produce the most explosive plays. Reveals defensive weaknesses against specific alignments.",
      "Run Concept": "Breaks down which run schemes (Inside Zone, Outside Zone, Power, etc.) are most effective. Shows which concepts the defense can't stop.",
      "Point of Attack": "Shows where successful runs hit the line of scrimmage (LE = Left End, ML = Middle Left, etc.). Identifies weak spots in the defensive front.",
      "Box Players": "Analyzes explosive runs by number of defenders in the box. Lower numbers may indicate light boxes that can't stop the run.",
      "Defensive Personnel": "Shows which defensive packages (4-3-4, 3-4-4, etc.) give up the most explosive runs. Helps identify vulnerable personnel groupings.",
      "Missed Tackles": "Individual defenders who missed tackles on explosive runs. Identifies players to target in game planning.",
      "Pass Depth": "Categorizes explosive passes by how far downfield they were thrown. Shows if big plays come from deep throws or YAC on shorter passes.",
      "Yards After Catch": "Breaks down how much YAC contributed to explosive passes. High YAC numbers indicate poor tackling or coverage breakdowns.",
      "Protections": "Shows explosive passes by number of pass blockers. More blockers may indicate better protection leading to big plays.",
      "Pass Routes and Depth": "Route group concepts with their average depth and defending players for explosive passes. Shows which route combinations, depths, and which defenders to target.",
      "Pass Coverage": "Analyzes explosive passes by number of defenders in coverage. Fewer defenders may indicate easier completions.",
      "Pass Coverage Defenders": "Individual defenders who gave up explosive passes with the depths and route codes. Shows which players to target, at what levels, and with which route concepts."
    };
    return explanations[title] || "";
  };

  const renderAnalysisTable = (title, data, showDefenders = false, sectionId = null) => (
    <Card sx={{ mb: 3 }} data-section={sectionId || `${title.toLowerCase().replace(/\s+/g, '-')}-section`}>
      <CardContent>
        <Typography variant="h6" gutterBottom>{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
          {getExplanation(title)}
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Category</strong></TableCell>
                <TableCell align="right"><strong>Count</strong></TableCell>
                <TableCell align="right"><strong>Percentage</strong></TableCell>
                {showDefenders && <TableCell><strong>Defenders</strong></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{item.category}</TableCell>
                  <TableCell align="right">{item.count}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${item.percentage}%`}
                      color={parseFloat(item.percentage) > 20 ? 'warning' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  {showDefenders && <TableCell>{item.defenders || '-'}</TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );

  const renderCoverageDefenders = (defenders, total) => (
    <Card sx={{ mb: 3 }} data-section="pass-coverage-defenders-section">
      <CardContent>
        <Typography variant="h6" gutterBottom>Pass Coverage Defenders</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
          Individual defenders who gave up explosive passes with the depths and route codes. Shows which players to target, at what levels, and with which route concepts.
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Defender</strong></TableCell>
                <TableCell align="right"><strong>Count</strong></TableCell>
                <TableCell align="right"><strong>Percentage</strong></TableCell>
                <TableCell><strong>Pass Depths</strong></TableCell>
                <TableCell><strong>Route Codes</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(defenders)
                .sort(([,a], [,b]) => b.count - a.count)
                .map(([defender, data], index) => (
                <TableRow key={index}>
                  <TableCell><strong>{defender}</strong></TableCell>
                  <TableCell align="right">{data.count}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${((data.count / total) * 100).toFixed(1)}%`}
                      color={((data.count / total) * 100) > 15 ? 'error' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{data.depths.join(', ')}</TableCell>
                  <TableCell>{data.routes.join(', ')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowLeft size={16} />}
          onClick={() => navigate('/reports')}
          variant="outlined"
        >
          Back to Reports
        </Button>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Shield size={32} />
          Defensive Vulnerability Report
        </Typography>
      </Box>

      <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
        Analyze defensive vulnerabilities by examining explosive runs (10+ yards) and explosive passes (15+ yards). 
        Identify offensive tendencies that work and defensive weaknesses to exploit.
      </Typography>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* File Upload Section */}
      {!reportData && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Upload size={24} />
              Upload PFF CSV Data
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileChange}
                style={{ display: 'none' }}
                id="csv-file-upload"
              />
              <label htmlFor="csv-file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<Upload size={16} />}
                  sx={{ mr: 2 }}
                >
                  Choose CSV File
                </Button>
              </label>
              {file && (
                <Chip
                  label={file.name}
                  onDelete={() => setFile(null)}
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>

            <Button
              variant="contained"
              onClick={handleGenerateReport}
              disabled={!file || loading}
              startIcon={loading ? <CircularProgress size={16} /> : <FileText size={16} />}
            >
              {loading ? 'Analyzing Defense...' : 'Generate Vulnerability Report'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Report Results */}
      {reportData && (
        <>
          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setReportData(null);
                setFile(null);
              }}
            >
              Generate New Report
            </Button>
            <Button
              variant="contained"
              onClick={saveReportAsPDF}
              startIcon={<FileText size={16} />}
            >
              Save as PDF
            </Button>
          </Box>

          <Box id="defensive-vulnerability-report-content">
            {/* Report Header */}
            <ReportHeader 
              title="DEFENSIVE VULNERABILITY ANALYSIS"
              fileName={file?.name}
            />
            
            {/* Summary Stats */}
            <Card sx={{ mb: 4 }} data-section="summary-section">
              <CardContent>
                <Typography variant="h5" gutterBottom>Vulnerability Summary</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Overview of explosive plays allowed by the defense. Explosive runs are 10+ yard gains, explosive passes are 15+ yard gains.
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ textAlign: 'center', bgcolor: 'success.light', p: 3, borderRadius: 2 }}>
                      <Typography variant="h3" sx={{ color: 'success.contrastText' }}>
                        {reportData.runs.total}
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'success.contrastText' }}>
                        Explosive Runs Allowed
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'success.contrastText' }}>
                        Runs of 10+ yards
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ textAlign: 'center', bgcolor: 'info.light', p: 3, borderRadius: 2 }}>
                      <Typography variant="h3" sx={{ color: 'info.contrastText' }}>
                        {reportData.passes.total}
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'info.contrastText' }}>
                        Explosive Passes Allowed
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'info.contrastText' }}>
                        Passes of 15+ yards
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Explosive Runs Analysis */}
            {reportData.runs.total > 0 && (
              <Accordion defaultExpanded sx={{ mb: 4 }}>
                <AccordionSummary expandIcon={<ChevronDown size={20} />}>
                  <Typography variant="h5">Explosive Runs Analysis ({reportData.runs.total} plays)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box data-section="explosive-runs-section">
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                      Analysis of all run plays that gained 10 or more yards. Shows offensive tendencies that work and defensive vulnerabilities to exploit.
                    </Typography>

                    {/* Offense Subsection */}
                    <Typography variant="h6" sx={{ color: 'primary.main', mb: 2, borderBottom: 1, borderColor: 'primary.light', pb: 1 }}>
                      Offensive Patterns
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      What the offense was doing when they broke explosive runs - personnel, formations, and concepts that worked.
                    </Typography>

                    {renderAnalysisTable("Offensive Personnel", calculatePercentages(reportData.runs.offense.personnel, reportData.runs.total), false, "offensive-personnel-section")}
                    {renderAnalysisTable("Formation", calculatePercentages(reportData.runs.offense.formation, reportData.runs.total), false, "formation-section")}
                    {renderAnalysisTable("Run Concept", calculatePercentages(reportData.runs.offense.runConcept, reportData.runs.total), false, "run-concept-section")}
                    {renderAnalysisTable("Point of Attack", calculatePercentages(reportData.runs.offense.pointOfAttack, reportData.runs.total), false, "point-of-attack-section")}

                    {/* Defense Subsection */}
                    <Typography variant="h6" sx={{ color: 'error.main', mb: 2, borderBottom: 1, borderColor: 'error.light', pb: 1, mt: 4 }}>
                      Defensive Vulnerabilities
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      What the defense was doing when they gave up explosive runs - personnel, alignments, and missed tackles to target.
                    </Typography>

                    {renderAnalysisTable("Box Players", calculatePercentages(reportData.runs.defense.boxPlayers, reportData.runs.total), false, "box-players-section")}
                    {renderAnalysisTable("Defensive Personnel", calculatePercentages(reportData.runs.defense.personnel, reportData.runs.total), false, "defensive-personnel-section")}
                    {renderAnalysisTable("Missed Tackles", calculatePercentages(reportData.runs.defense.missedTackles, reportData.runs.total), false, "missed-tackles-section")}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Explosive Passes Analysis */}
            {reportData.passes.total > 0 && (
              <Accordion defaultExpanded sx={{ mb: 4 }}>
                <AccordionSummary expandIcon={<ChevronDown size={20} />}>
                  <Typography variant="h5">Explosive Passes Analysis ({reportData.passes.total} plays)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box data-section="explosive-passes-section">
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                      Analysis of all pass plays that gained 15 or more yards. Identifies passing concepts that work and coverage weaknesses to attack.
                    </Typography>

                    {/* Offense Subsection */}
                    <Typography variant="h6" sx={{ color: 'primary.main', mb: 2, borderBottom: 1, borderColor: 'primary.light', pb: 1 }}>
                      Offensive Patterns
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Offensive patterns that produced explosive passes - personnel, formations, depths, routes, protection schemes, and YAC opportunities.
                    </Typography>

                    {renderAnalysisTable("Offensive Personnel", calculatePercentages(reportData.passes.offense.personnel, reportData.passes.total), false, "pass-offensive-personnel-section")}
                    {renderAnalysisTable("Formation", calculatePercentages(reportData.passes.offense.formation, reportData.passes.total), false, "pass-formation-section")}
                    {renderAnalysisTable("Pass Depth", calculatePercentages(reportData.passes.offense.passDepth, reportData.passes.total), false, "pass-depth-section")}
                    {renderAnalysisTable("Yards After Catch", calculatePercentages(reportData.passes.offense.yardsAfterCatch, reportData.passes.total), false, "yards-after-catch-section")}
                    {renderAnalysisTable("Protections", calculatePercentages(reportData.passes.offense.protections, reportData.passes.total), false, "protections-section")}
                    {renderAnalysisTable("Pass Routes and Depth", calculateRoutePercentages(reportData.passes.offense.passRoutes, reportData.passes.total), true, "pass-routes-and-depth-section")}

                    {/* Defense Subsection */}
                    <Typography variant="h6" sx={{ color: 'error.main', mb: 2, borderBottom: 1, borderColor: 'error.light', pb: 1, mt: 4 }}>
                      Defensive Vulnerabilities
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Defensive coverage patterns when explosive passes occurred - coverage schemes and individual defenders to target.
                    </Typography>

                    {renderAnalysisTable("Pass Coverage", calculatePercentages(reportData.passes.defense.passCoverage, reportData.passes.total), false, "pass-coverage-section")}
                    {renderCoverageDefenders(reportData.passes.defense.coverageDefenders, reportData.passes.total)}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        </>
      )}

      {/* Instructions */}
      <Card sx={{ mt: 4, bgcolor: 'background.default' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Required CSV Columns</Typography>
          <Typography variant="body2" component="div">
            <strong>Core Requirements:</strong>
            <ul>
              <li><strong>pff_RUNPASS:</strong> Contains 'R' for runs, 'P' for passes</li>
              <li><strong>pff_GAINLOSS:</strong> Yardage gained/lost on each play</li>
            </ul>
            <strong>Additional Columns (optional but recommended):</strong>
            <ul>
              <li>pff_OFFPERSONNELBASIC, pff_OFFFORMATIONGROUP, pff_RUNCONCEPTPRIMARY, pff_POAACTUAL</li>
              <li>pff_BOXPLAYERS, pff_DEFPERSONNEL, pff_MISSEDTACKLE</li>
              <li>pff_PASSDEPTH, pff_YARDSAFTERCATCH, pff_PASSBLOCKING, pff_PASSROUTETARGETGROUP</li>
              <li>pff_PASSCOVERAGEPLAYERS, pff_PASSCOVERAGE1</li>
            </ul>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DefensiveVulnerabilityReportGenerator;